/** Peças visuais partilhadas pelas abas de Ads: cabeçalho ordenável, paginação de 50 e chip do farol. */
import { memo, useMemo, useState } from 'react'
import { Box, Chip, Stack, TableCell, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material'
import { farolCor, farolLabel, ordenar, type Dir, type Farol } from './logic'

export const POR_PAGINA = 50

export function useOrdenacao<T>(linhas: T[], chaveInicial: keyof T, dirInicial: Dir = 'desc') {
  const [chave, setChave] = useState<keyof T>(chaveInicial)
  const [dir, setDir] = useState<Dir>(dirInicial)
  const [pagina, setPagina] = useState(0)
  const ordenadas = useMemo(() => ordenar(linhas, chave, dir), [linhas, chave, dir])
  const visiveis = useMemo(() => ordenadas.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA), [ordenadas, pagina])
  const alternar = (k: keyof T) => {
    if (k === chave) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setChave(k); setDir('desc') }
    setPagina(0)
  }
  return { chave, dir, pagina, setPagina, ordenadas, visiveis, alternar }
}

export function Th<T>({ k, label, chave, dir, onClick, align = 'right', hint }: {
  k: keyof T; label: string; chave: keyof T; dir: Dir; onClick: (k: keyof T) => void; align?: 'left' | 'right'; hint?: string
}) {
  const cab = (
    <TableSortLabel active={chave === k} direction={chave === k ? dir : 'desc'} onClick={() => onClick(k)}>
      {label}
    </TableSortLabel>
  )
  return <TableCell align={align} sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{hint ? <Tooltip title={hint}>{cab}</Tooltip> : cab}</TableCell>
}

export const Paginacao = memo(function Paginacao({ total, pagina, setPagina }: { total: number; pagina: number; setPagina: (p: number) => void }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))
  if (paginas <= 1) return <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{total} linha(s)</Typography>
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{total} linhas · página {pagina + 1} de {paginas}</Typography>
      <Chip size="small" label="‹ anterior" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)} />
      <Chip size="small" label="próxima ›" disabled={pagina >= paginas - 1} onClick={() => setPagina(pagina + 1)} />
    </Stack>
  )
})

export const FarolChip = memo(function FarolChip({ farol, pequeno }: { farol: Farol; pequeno?: boolean }) {
  const t = useTheme()
  const cor = farolCor(farol, t.palette)
  return (
    <Chip
      size="small"
      label={farolLabel(farol)}
      sx={{ bgcolor: cor, color: '#fff', fontWeight: 700, fontSize: pequeno ? 10 : 11, height: pequeno ? 18 : 22 }}
    />
  )
})

export function Vazio({ texto }: { texto: string }) {
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography color="text.secondary">{texto}</Typography>
    </Box>
  )
}
